import '@explorins/pers-shared';
import { b as buildPaginationParams, n as normalizeToPaginated } from './pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic Tenant API Client
 *
 * Handles tenant and admin operations using the PERS backend.
 * Updated to match new RESTful tenant controller endpoints.
 *
 * Note: Special header handling (bypass-auth-interceptor) should be handled by PersApiClient internally
 * or through endpoint-specific configuration.
 */
class TenantApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/tenants';
        this.adminPath = '/admins';
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * API KEY + ADMIN JWT: Get full tenant information including sensitive config
     * Uses /tenants/me endpoint with Admin JWT authentication
     *
     * Requires: X-API-Key header + Authorization Bearer token (Admin JWT)
     * Returns: Full tenant information including allowedTokenIssuers and sensitive config
     */
    async getRemoteTenant() {
        const timestamp = Date.now().toString();
        const url = `${this.basePath}/me?date=${timestamp}`;
        return this.apiClient.get(url);
    }
    /**
     * API KEY: Get current tenant public information
     * Uses /tenants/me endpoint with API key authentication
     *
     * Requires: X-API-Key header (handled by PersApiClient)
     * Returns: Public tenant information (no sensitive config)
     */
    async getRemoteTenantPublic() {
        const timestamp = Date.now().toString();
        const url = `${this.basePath}/me?date=${timestamp}`;
        return this.apiClient.get(url);
    }
    /**
     * API KEY: Get remote login token
     * UPDATED: Requires API key authentication
     */
    async getRemoteLoginToken() {
        return this.apiClient.get(`${this.basePath}/login-token`);
    }
    /**
     * API KEY: Get remote client configuration
     * UPDATED: Requires API key authentication
     */
    async getRemoteClientConfig() {
        return this.apiClient.get(`${this.basePath}/client-config`);
    }
    // ==========================================
    // PUBLIC UNAUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get tenant public information by ID (no auth required)
     * NEW: For cases where you need public tenant info without API key
     *
     * @param tenantId - The tenant ID to retrieve public information for
     */
    async getPublicTenantById(tenantId) {
        const url = `${this.basePath}/${tenantId}`;
        return this.apiClient.get(url);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Get current admin info (tenant admin authentication required)
     *
     * Endpoint: GET /admins/me
     * Auth: @Admin()
     */
    async getCurrentAdmin() {
        return this.apiClient.get(`${this.adminPath}/me`);
    }
    /**
     * ADMIN: Update tenant information
     * UPDATED: Uses PUT /tenants (admin endpoint)
     * Returns full TenantDTO including sensitive config
     */
    async updateRemoteTenant(tenantData) {
        return this.apiClient.put(`${this.basePath}`, tenantData);
    }
    /**
     * ADMIN: Get all tenant admins
     *
     * @param options Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with tenant admins
     */
    async getAdmins(options) {
        const params = buildPaginationParams(options);
        const response = await this.apiClient.get(`${this.adminPath}?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * ADMIN: Create new admin
     */
    async postAdmin(adminData) {
        return this.apiClient.post(`${this.adminPath}`, adminData);
    }
    /**
     * ADMIN: Update admin (toggle tenant association)
     */
    async putAdmin(adminId, adminData) {
        return this.apiClient.put(`${this.adminPath}/${adminId}/tenant`, adminData);
    }
}

/**
 * Platform-Agnostic Tenant Service
 *
 * Contains tenant business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 * Matches framework TenantApiService capabilities exactly.
 */
class TenantService {
    constructor(tenantApi) {
        this.tenantApi = tenantApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * Get full tenant information (Admin JWT required)
     */
    async getRemoteTenant() {
        return this.tenantApi.getRemoteTenant();
    }
    /**
     * PUBLIC: Get tenant public information (API key only)
     */
    async getRemoteTenantPublic() {
        return this.tenantApi.getRemoteTenantPublic();
    }
    /**
     * PUBLIC: Get remote login token
     */
    async getRemoteLoginToken() {
        return this.tenantApi.getRemoteLoginToken();
    }
    /**
     * PUBLIC: Get remote client configuration
     */
    async getRemoteClientConfig() {
        return this.tenantApi.getRemoteClientConfig();
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Get current admin info (tenant admin authentication required)
     */
    async getCurrentAdmin() {
        return this.tenantApi.getCurrentAdmin();
    }
    /**
     * ADMIN: Update tenant information
     * Returns full TenantDTO including sensitive config
     */
    async updateRemoteTenant(tenantData) {
        return this.tenantApi.updateRemoteTenant(tenantData);
    }
    /**
     * ADMIN: Get all tenant admins with pagination
     * @param options - Pagination options
     */
    async getAdmins(options) {
        return this.tenantApi.getAdmins(options);
    }
    /**
     * ADMIN: Create new admin
     * ✅ FIXED: Renamed to match framework postAdmin method
     */
    async postAdmin(adminData) {
        return this.tenantApi.postAdmin(adminData);
    }
    /**
     * ADMIN: Update admin (toggle tenant association)
     * ✅ FIXED: Renamed to match framework putAdmin method
     */
    async putAdmin(adminId, adminData) {
        return this.tenantApi.putAdmin(adminId, adminData);
    }
}

export { TenantApi as T, TenantService as a };
//# sourceMappingURL=tenant-service-DlhdZAs9.js.map
